import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { SUPPORTED_LOCALES } from '@/shared/i18n'
import { useTheme } from '@/shared/theme/ThemeContext'

// Planos y con borde, como el set de botones de Safyro Finanzas
// (apps/web/src/ui/button.css: .btn--primary/--secondary/--ghost) — nada de
// degradados en la interfaz, el acento vive en el color sólido.
const VARIANTS = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary: 'bg-(--humania-surface) text-ink-900 border border-ink-200 hover:bg-ink-50',
  success: 'bg-ok-50 text-ok-500 hover:brightness-95',
  danger: 'bg-danger-50 text-danger-500 hover:brightness-95',
  ghost: 'text-brand-600 hover:bg-brand-50',
}

export function Button({ variant = 'primary', className, disabled, children, ...props }) {
  return (
    <button
      className={clsx(
        'inline-flex min-h-10.5 items-center justify-center gap-2 rounded-xl px-4.5 text-sm font-semibold',
        'transition disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        VARIANTS[variant],
        className
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
}

// Mismo aspecto que Button pero para navegación (react-router <Link>, <a>).
export function LinkButton({
  as: Component = 'a',
  variant = 'primary',
  className,
  children,
  ...props
}) {
  return (
    <Component
      className={clsx(
        'inline-flex min-h-10.5 items-center justify-center gap-2 rounded-xl px-4.5 text-sm font-semibold',
        'transition',
        VARIANTS[variant],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

const INPUT_BASE =
  'w-full min-h-11 rounded-xl border bg-(--humania-surface) px-[14px] py-2 text-sm text-ink-900 transition ' +
  'focus:border-brand-500 focus:shadow-[0_0_0_4px_rgba(11,61,180,.10)] focus:outline-none'

export function Field({ label, id, error, ...props }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink-600">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={clsx(INPUT_BASE, error ? 'border-danger-500' : 'border-ink-200')}
        {...props}
      />
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger-500">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export function TextAreaField({ label, id, error, rows = 5, ...props }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink-600">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={clsx(
          INPUT_BASE,
          'py-3 leading-6',
          error ? 'border-danger-500' : 'border-ink-200'
        )}
        {...props}
      />
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger-500">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export function SelectField({ label, id, error, children, ...props }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink-600">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={clsx(INPUT_BASE, error ? 'border-danger-500' : 'border-ink-200')}
        {...props}
      >
        {children}
      </select>
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger-500">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Muestra un error del backend. El `request_id` es lo que el usuario le pasa
 * a soporte para que se pueda rastrear el incidente en los logs.
 */
export function ErrorNotice({ error }) {
  const { t } = useTranslation()
  if (!error) return null

  return (
    <div role="alert" className="rounded-lg border border-danger-500/30 bg-danger-500/5 p-3">
      <p className="text-sm font-medium text-danger-500">{error.title}</p>
      {error.detail && error.detail !== error.title ? (
        <p className="mt-1 text-xs text-ink-400">{error.detail}</p>
      ) : null}
      {error.requestId ? (
        <p className="mt-2 font-mono text-[11px] text-ink-400">
          {t('errors.requestId', { id: error.requestId })}
        </p>
      ) : null}
    </div>
  )
}

export function Card({ className, interactive = false, children, ...props }) {
  return (
    <div
      className={clsx(
        'rounded-card border border-ink-200 bg-(--humania-surface) p-6 shadow-card',
        interactive &&
          'transition hover:-translate-y-1 hover:border-brand-500/25 hover:shadow-card-hover',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const BADGE_TONES = {
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-ok-50 text-ok-500',
  warn: 'bg-warn-50 text-warn-500',
  danger: 'bg-danger-50 text-danger-500',
  neutral: 'bg-ink-100 text-ink-600',
}

export function Badge({ tone = 'brand', className, children }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
        BADGE_TONES[tone] ?? BADGE_TONES.brand,
        className
      )}
    >
      {children}
    </span>
  )
}

/**
 * Tarjeta de métrica de la hoja de marca: valor grande a la izquierda e icono
 * en pastilla a la derecha.
 */
export function StatCard({ label, value, icon, hint, className }) {
  return (
    <Card className={clsx('flex items-start justify-between gap-4 p-5', className)}>
      <div className="min-w-0">
        <p className="text-sm text-ink-400">{label}</p>
        <p className="mt-2 text-3xl font-bold tracking-tight">{value}</p>
        {hint ? <p className="mt-1 text-xs text-ink-400">{hint}</p> : null}
      </div>
      {icon ? (
        <span className="grid size-11 shrink-0 place-items-center rounded-[14px] bg-brand-50">
          {icon}
        </span>
      ) : null}
    </Card>
  )
}

/** Iniciales sobre degradado: no hay fotos de perfil en el producto. */
export function Avatar({ name, className }) {
  const initials = (name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <span
      aria-hidden="true"
      className={clsx(
        'grid size-10 shrink-0 place-items-center rounded-full bg-brand-600',
        'text-sm font-semibold text-white',
        className
      )}
    >
      {initials || '?'}
    </span>
  )
}

/**
 * Banner degradado de la hoja de marca. El texto va como children en vez de
 * usar los SVG de `images/banners`, que traen el copy en español incrustado y
 * no se pueden traducir.
 */
export function Banner({ title, subtitle, actions, className }) {
  return (
    <section className={clsx('humania-banner', className)}>
      <h2 className="max-w-xl text-[1.75rem] font-bold leading-tight text-white">{title}</h2>
      {subtitle ? <p className="mt-2 max-w-xl text-sm text-white/85">{subtitle}</p> : null}
      {actions ? <div className="mt-5 flex flex-wrap gap-3">{actions}</div> : null}
    </section>
  )
}

export function Spinner({ label }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3 text-ink-400" role="status" aria-live="polite">
      <span
        className="size-4 animate-spin rounded-full border-2 border-ink-100 border-t-brand-600"
        aria-hidden="true"
      />
      <span className="text-sm">{label ?? t('common.loading')}</span>
    </div>
  )
}

/**
 * Placeholder de carga con la forma del contenido final (mismo patrón que
 * Safyro Finanzas: ui/Skeleton.tsx), en vez de un spinner que tapa la
 * pantalla. Úsalo para listas, tarjetas y campos de formulario mientras
 * llega la respuesta.
 */
export function Skeleton({ width = '100%', height = 16, radius, className }) {
  return (
    <span
      aria-hidden="true"
      className={clsx('humania-skeleton', className)}
      style={{ width, height, borderRadius: radius ?? 'var(--humania-radius-sm)' }}
    />
  )
}

/** Filas de skeleton del alto de un input — para formularios cargando datos previos. */
export function SkeletonField({ labelWidth = '30%' }) {
  return (
    <div className="space-y-1.5">
      <Skeleton width={labelWidth} height={12} />
      <Skeleton height={44} radius="var(--humania-radius-sm)" />
    </div>
  )
}

/** Tarjeta de skeleton del tamaño de una `Card` — para grillas de resultados cargando. */
export function SkeletonCard({ lines = 3, className }) {
  return (
    <Card className={className}>
      <Skeleton width="45%" height={14} />
      <div className="mt-4 space-y-2">
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} height={12} width={i === lines - 1 ? '65%' : '100%'} />
        ))}
      </div>
    </Card>
  )
}

export function PageHeader({ title, subtitle, actions, className }) {
  return (
    <header className={clsx('flex flex-wrap items-end justify-between gap-4', className ?? 'mb-8')}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-ink-400">{subtitle}</p> : null}
      </div>
      {actions}
    </header>
  )
}

export function EmptyState({ message }) {
  return (
    <Card className="text-center text-sm text-ink-400">
      <p>{message}</p>
    </Card>
  )
}

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation()
  const current = i18n.resolvedLanguage ?? 'es'

  return (
    <label className="flex items-center gap-2 text-sm text-ink-400">
      <span className="sr-only">{t('nav.language')}</span>
      <select
        className="rounded-md border border-ink-100 bg-(--humania-surface) px-2 py-1 text-ink-600"
        value={current}
        onChange={(event) => i18n.changeLanguage(event.target.value)}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale.code} value={locale.code}>
            {locale.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function SunIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="12" cy="12" r="4" />
      <path
        strokeLinecap="round"
        d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8"
      />
    </svg>
  )
}

function MoonIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"
      />
    </svg>
  )
}

/**
 * Alterna claro/oscuro, mismo patrón que Safyro Finanzas/Contadores: sigue al
 * sistema hasta que se elige explícito, y a partir de ahí queda fijo.
 * `dark` recibe el toggle en fondo oscuro (sidebar/topbar) donde el botón
 * plano de `LanguageSwitcher` no se lee.
 */
export function ThemeToggle({ dark = false, className }) {
  const { theme, toggle } = useTheme()
  const { t } = useTranslation()
  const isDark = theme === 'dark'

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? t('nav.themeLight') : t('nav.themeDark')}
      title={isDark ? t('nav.themeLight') : t('nav.themeDark')}
      className={clsx(
        'grid size-9 shrink-0 place-items-center rounded-lg transition',
        dark
          ? 'text-white/80 hover:bg-white/10 hover:text-white'
          : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900',
        className
      )}
    >
      {isDark ? <SunIcon className="size-4.5" /> : <MoonIcon className="size-4.5" />}
    </button>
  )
}
